import React from 'react';
import { AlertTriangle, ExternalLink, RotateCcw } from 'lucide-react';

// Sep 2026 · full-width explanation under a greyed-out company card in the
// outreach dialog: WHY it is held back (logged signal quote + date, or a soft
// quality-gate verdict) so the BD lead can cross-check and reinstate in place.
export const HeldBackBanner = ({ companyName, softReason, signalAudit, profileId, onReinstate }) => {
  if (!softReason) return null;
  const isSignal = !!(signalAudit && signalAudit.verdict === 'conflict');
  const tone = isSignal ? 'bg-rose-50 border-rose-300 text-rose-900' : 'bg-amber-50 border-amber-300 text-amber-900';
  const signals = (signalAudit?.signals || []).slice(0, 4);
  return (
    <div className={`px-4 py-2.5 border-y ${tone}`} data-testid={`held-back-banner-${companyName}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-xs font-semibold">
            {isSignal ? 'Greyed out — a signal you logged on this company conflicts with this asset' : `Greyed out — needs your call: ${softReason}`}
          </p>
          {isSignal && <p className="text-[11px] leading-snug">{signalAudit.reason}</p>}
          {isSignal && signals.length > 0 && (
            <ul className="space-y-0.5" data-testid={`held-back-signals-${companyName}`}>
              {signals.map((s, i) => (
                <li key={i} className="text-[11px] flex flex-wrap items-baseline gap-x-1.5">
                  <span className={`px-1 rounded text-[9px] font-semibold uppercase ${s.polarity === 'negative' ? 'bg-rose-200 text-rose-900' : 'bg-emerald-100 text-emerald-800'}`}>{s.polarity === 'negative' ? 'not interested' : 'focus'}</span>
                  <span className="font-medium">{s.area}</span>
                  {s.status && <span className="opacity-70">· {s.status}</span>}
                  {s.date && <span className="opacity-70">· {s.date}</span>}
                  {s.evidence && <span className="italic opacity-90">— “{s.evidence}”</span>}
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-3 pt-0.5">
            {isSignal && profileId && (
              <a href={`/company-profile/${profileId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-semibold underline" data-testid={`held-back-profile-link-${companyName}`}>
                Cross-check signals on the profile <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <button type="button" onClick={onReinstate} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 hover:underline" data-testid={`held-back-reinstate-${companyName}`}>
              <RotateCcw className="w-3 h-3" /> Reinstate with rationale
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
